import { useCameraPermission } from '@/stores/permissions/camera-permission.store';
import { usePhotoGalleryPermission } from '@/stores/permissions/gallery-permission.store';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { usePermissionAlert } from './use-permission-alert';

type PhotoResult =
  | { error: false; base64: string; errorMsg?: undefined }
  | { error: true; base64?: undefined; errorMsg?: string };

export function useCamera() {
  const { showAlert } = usePermissionAlert('Camera/Photos');
  const cameraPermission = useCameraPermission();
  const photoPermission = usePhotoGalleryPermission();
  async function getPhoto(): Promise<PhotoResult> {
    await cameraPermission.prompt();
    await photoPermission.prompt();
    const source: CameraSource | null =
      cameraPermission.ready && photoPermission.ready
        ? CameraSource.Prompt
        : cameraPermission.ready
        ? CameraSource.Camera
        : photoPermission.ready
        ? CameraSource.Photos
        : null;
    if (source) {
      try {
        const res = await Camera.getPhoto({
          resultType: CameraResultType.Base64,
          quality: 50,
          source,
        });
        if (res?.base64String) {
          return {
            error: false,
            base64: res.base64String,
          };
        } else {
          return {
            error: true,
            errorMsg: 'Unable to get photo',
          };
        }
      } catch (e) {
        return {
          error: true,
          errorMsg:
            e instanceof Error && e.message?.includes('cancelled')
              ? undefined
              : e instanceof Error
              ? e.message
              : 'Unable to get photo',
        };
      }
    } else {
      showAlert();
      return {
        error: true,
        errorMsg: `No Access to Camera`,
      };
    }
  }

  return { getPhoto };
}
